import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "lucide-react";
import api, { mediaSrc, thumbSrc } from "@/lib/api";
import { Reveal, SectionHeading } from "./Reveal";

const isEmbed = (url = "") => /youtube|youtu\.be|vimeo/.test(url);
const toEmbedUrl = (url = "") => {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return url;
};

const FilmCard = ({ film, index, large, onPlay }) => {
  const thumb = thumbSrc(film);
  return (
    <motion.button
      data-testid={`showreel-card-${index}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => onPlay(film)}
      className={`group relative text-left border border-ivory/10 overflow-hidden hover:border-amber/40 transition-colors duration-500 ${
        large ? "md:col-span-7" : "md:col-span-5"
      }`}
    >
      <div className="relative aspect-video overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            alt={film.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.05] transition-all duration-[1.2s]"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-raise to-ink" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-ink/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20">
            <span className="absolute inset-0 rounded-full border border-amber/60 animate-ping opacity-30" />
            <span className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-ivory/10 backdrop-blur-md border border-ivory/30 flex items-center justify-center group-hover:bg-amber group-hover:border-amber transition-all duration-500">
              <Play size={18} className="fill-ivory text-ivory group-hover:fill-ink group-hover:text-ink ml-0.5 transition-colors duration-500" />
            </span>
          </span>
        </div>
        {film.specs && (
          <span className="absolute top-4 right-4 font-mono text-[9px] tracking-[0.25em] uppercase text-cream border border-ivory/20 bg-ink/60 backdrop-blur px-3 py-1.5">
            {film.specs}
          </span>
        )}
      </div>
      <div className="sprocket-strip opacity-60" />
      <div className="p-5 bg-coal flex items-baseline justify-between gap-4">
        <h3 className="font-display text-base sm:text-lg text-ivory tracking-wide">{film.title}</h3>
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ash shrink-0">
          {film.location || film.category}
        </span>
      </div>
    </motion.button>
  );
};

export const Showreel = () => {
  const [films, setFilms] = useState([]);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api
      .get("/media", { params: { kind: "film" } })
      .then((r) => setFilms(r.data))
      .catch(() => setFilms([]));
  }, []);

  useEffect(() => {
    document.body.style.overflow = playing ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [playing]);

  return (
    <section id="films" data-testid="showreel-section" className="relative py-28 sm:py-36 bg-coal/40 border-y border-ivory/5">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-10">
        <Reveal className="mb-4">
          <p className="font-serif italic text-lg text-ash">Motion pictures, graded by hand</p>
        </Reveal>
        <div className="mb-14">
          <SectionHeading eyebrow="Films" title="The Showreel" testid="showreel-heading" />
        </div>

        {films.length === 0 ? (
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-mute border border-dashed border-line p-16 text-center">
            Films are being graded — premiering soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {films.map((film, i) => (
              <FilmCard key={film.id} film={film} index={i} large={i === 0} onPlay={setPlaying} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {playing && (
          <motion.div
            data-testid="showreel-player-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPlaying(null)}
            className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-12"
          >
            <button
              data-testid="showreel-player-close"
              aria-label="Close player"
              onClick={() => setPlaying(null)}
              className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center border border-ivory/20 text-ivory hover:border-amber hover:text-amber transition-colors z-10"
            >
              <X size={16} />
            </button>
            <motion.div
              initial={{ scale: 0.95, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 24 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-5xl"
            >
              <div className="sprocket-strip mb-2 opacity-70" />
              <div className="aspect-video bg-black border border-ivory/10 shadow-[0_0_80px_rgba(229,192,123,0.12)]">
                {isEmbed(playing.external_url || "") ? (
                  <iframe
                    src={toEmbedUrl(playing.external_url)}
                    title={playing.title}
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    data-testid="showreel-video-player"
                    src={mediaSrc(playing)}
                    controls
                    autoPlay
                    controlsList="nodownload noremoteplayback"
                    disablePictureInPicture
                    disableRemotePlayback
                    onContextMenu={(e) => e.preventDefault()}
                    className="w-full h-full"
                  />
                )}
              </div>
              <div className="sprocket-strip mt-2 opacity-70" />
              <p className="font-display text-lg text-ivory tracking-wide mt-4">{playing.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
